import { IRouter } from '../types';
import { routeAuth } from '../utils';
import { MaxRouterHistory } from './MaxRouterHistory';
import { cachePageInstances } from '../share';

export function Router(): IRouter & { maxRouterHistory: MaxRouterHistory } {
  // 创建路由历史管理实例
  // MaxRouterHistory 是一个专门用于管理路由历史和页面渲染的类，提供了路由跳转、后退、重定向等功能
  const maxRouterHistory = new MaxRouterHistory();

  /**
   * 导航到指定的URL路径
   * @template T - 路由参数的类型
   * @param url - 目标导航路径
   * @param params - 可选的路由参数，类型为泛型T
   * @returns void
   *
   * @description 该函数实现了应用内页面跳转的核心逻辑，主要步骤包括：
   * 1. 权限验证 - 确保用户有权限访问目标路径
   * 2. 记录上一个路由信息
   * 3. 执行路由跳转并传递参数
   * 4. 触发路由变化事件，通知监听器当前路由和上一个路由的信息
   */
  function navigateTo<T = any>(url: string, params: T) {
    // 权限验证：调用routeAuth函数检查是否有权限访问目标URL
    // 如果无权限，则直接返回，不执行后续导航逻辑
    if (!routeAuth(url)) return;

    maxRouterHistory.go(url, params);
  }

  /**
   * 后退指定步数的路由
   * @template T - 路由参数的类型
   * @param num - 后退的步数，默认为1步
   * @param params - 可选的路由参数，类型为泛型T，将传递给目标路由
   * @returns void
   *
   * @description 该函数实现了应用内页面后退的核心逻辑，支持多级后退并可以传递新的参数。
   * 主要步骤包括：
   * 1. 保存当前路由信息作为"上一个路由"
   * 2. 执行路由后退操作，获取新的当前路由
   * 3. 创建不可变的路由数据代理对象
   * 4. 触发路由变化事件通知监听器
   * 5. 记录路由变化的调试日志
   */
  function navigateBack<T = any>(num = 1, params?: T) {
    // 执行后退操作：调用MaxRouterHistory实例的back方法，后退指定步数
    // 同时可以传递新的参数，这将更新后退目标路由的参数
    maxRouterHistory.back(num, params);
  }

  /**
   * 返回到历史记录中的指定URL路径
   * @template T - 路由参数的类型
   * @param url - 目标导航路径，必须是历史记录中存在的路径
   * @param params - 可选的路由参数，类型为泛型T，将传递给目标路由
   * @returns void
   *
   * @description 该函数实现了应用内直接返回到历史记录中特定页面的功能，而不是仅按步数后退。
   * 主要步骤包括：
   * 1. 权限验证 - 确保用户有权限访问目标路径
   * 2. 保存当前路由信息作为"上一个路由"
   * 3. 执行路由后退到指定URL的操作
   * 4. 创建不可变的路由数据代理对象
   * 5. 触发路由变化事件通知监听器
   * 6. 记录路由变化的调试日志
   */
  function navigateBackTo<T = any>(url: string, params?: T) {
    // 权限验证：调用routeAuth函数检查是否有权限访问目标URL
    // 如果无权限，则直接返回，不执行后续导航逻辑
    if (!routeAuth(url)) return;

    // 执行路由后退到指定URL：调用MaxRouterHistory实例的backTo方法
    // 该方法会找到历史记录中匹配的URL并直接跳转过去，而不是按固定步数后退
    maxRouterHistory.backTo(url, params);
  }

  /**
   * 清除所有路由栈历史记录
   * @param pageKey - 可选参数，指定清除后跳转到的页面路径
   * @returns void
   *
   * @description 该函数用于完全清除路由历史栈，重置应用的导航状态。
   * 主要功能包括：
   * 1. 清除所有路由历史记录，重置路由栈为空
   * 2. 可选地，在清除后跳转到指定的新页面
   *
   * 该函数通常用于需要完全重置应用导航状态的场景，如：
   * - 用户登出后跳转到登录页面
   * - 应用从一个主要流程切换到另一个完全独立的流程
   * - 需要清空导航历史，防止用户通过后退按钮返回之前的页面
   */
  function clearAllStack(pageKey?: string) {
    // 清除所有路由历史记录：调用MaxRouterHistory实例的clear方法
    // 这将重置整个路由栈，删除所有之前的导航记录
    maxRouterHistory.clear();

    // 如果提供了pageKey参数，则在清除路由栈后跳转到该页面
    // 这种设计允许在重置应用导航状态的同时立即导航到新的起点
    if (pageKey) {
      maxRouterHistory.go(pageKey);
    }
  }

  /**
   * 执行路由重定向操作
   * 重定向会替换当前路由栈中的当前页面，不会在历史记录中保留当前页面
   *
   * @template T - 路由参数的数据类型，默认为any类型
   * @param {string} url - 目标重定向URL路径
   * @param {T} [params] - 可选的路由参数，可以是任意类型的数据
   * @returns {void}
   *
   * @description
   * 主要功能包括：
   * 1. 路由权限验证 - 确保只有具备访问权限的路由才能被重定向到
   * 2. 保存当前路由信息 - 用于页面间状态传递和动画过渡
   * 3. 执行重定向操作 - 替换当前路由页面
   * 4. 触发路由变化事件 - 向外暴露路由数据，支持应用响应路由变化
   * 5. 记录调试日志 - 便于开发调试和问题排查
   */
  function redirectTo<T = any>(url: string, params?: T) {
    // 路由权限验证，如果无权限则直接返回，不执行重定向
    if (!routeAuth(url)) return;

    // 执行重定向操作，替换当前路由页面
    maxRouterHistory.redirect(url, params);
  }

  /**
   * 获取所有路由栈信息
   *
   * @returns {Array<{ pageId: string; pageKey: string; instanceId: string }>} 返回格式化后的路由栈数组
   * @description
   * 主要功能包括：
   * 1. 从路由历史管理器中获取原始路由栈数据
   * 2. 将原始路由栈数据转换为标准化格式，统一使用URL作为页面标识
   * 3. 为每个路由生成包含pageId、pageKey和instanceId的标准化对象
   *
   * 该方法主要用于获取应用中所有已访问的页面列表，便于实现面包屑导航、历史记录展示等功能
   */
  function getAllStack() {
    // 从路由历史管理器中获取原始路由栈数据
    const routeStacks = maxRouterHistory.getRouteStacks();
    // 转换路由栈数据为标准化格式
    return routeStacks.map((routeStack) => {
      // 从原始路由栈中解构获取URL作为pageId
      const { pureRoute: pageId, search, params } = routeStack;
      // 返回标准化的路由信息对象，包含页面标识的不同表示形式
      return {
        pageId, // 页面ID，用于唯一标识页面
        pageKey: pageId, // 页面键，用于页面组件缓存或状态管理
        instanceId: pageId, // 实例ID，用于区分同页面的不同实例
        search,
        params,
      };
    });
  }

  /**
   * 获取当前路由信息
   *
   * @returns {Object} 返回格式化后的当前路由信息对象
   * @returns {string} returns.pageId - 当前页面的唯一标识ID，来自路由URL
   * @returns {string} returns.pageKey - 当前页面的键，用于页面组件缓存或状态管理
   * @returns {string} returns.instanceId - 当前页面实例的唯一标识
   * @returns {any} returns.params - 当前路由的参数对象
   * @description
   * 主要功能包括：
   * 1. 从路由历史管理器中获取当前路由栈信息
   * 2. 提取路由URL作为页面标识
   * 3. 保留当前路由的参数信息
   * 4. 返回标准化格式的当前路由数据对象
   *
   * 该方法主要用于获取当前显示页面的完整信息，便于应用程序获取当前页面状态、参数等数据
   */
  function getCurrent() {
    // 从路由历史管理器中获取当前路由栈信息，并解构出URL和参数
    const { pureRoute: pageId, search, params } = maxRouterHistory.getCurrentRouteStack();
    // 返回标准化格式的当前路由信息对象
    return {
      pageId, // 页面ID，基于当前路由URL
      pageKey: pageId, // 页面键，用于页面组件缓存机制
      instanceId: pageId, // 实例ID，用于标识当前页面实例
      params, // 当前路由的参数对象，包含路由传递的数据
      search,
    };
  }

  /**
   * 获取当前路由的参数对象
   *
   * @returns {any} 当前路由的参数对象，包含通过路由传递的数据
   * @description
   * 主要功能包括：
   * 1. 从路由历史管理器中获取当前路由栈信息
   * 2. 提取并返回路由参数对象
   *
   * 该方法提供了一种便捷的方式来访问当前页面的路由参数，便于页面组件获取从上一个页面传递的数据
   */
  function getParams() {
    // 从路由历史管理器中获取当前路由栈信息，并解构出参数对象
    const { params } = maxRouterHistory.getCurrentRouteStack();
    // 返回路由参数对象
    return params;
  }

  function getSearch() {
    // 从路由历史管理器中获取当前路由栈信息，并解构出参数对象
    const { search } = maxRouterHistory.getCurrentRouteStack();
    // 返回路由参数对象
    return search;
  }

  function reload(options: Record<string, any>) {
    const { pageId, params } = options;
    maxRouterHistory.clear();
    cachePageInstances.clear();
    maxRouterHistory.maxPageRender.clear();
    navigateTo(pageId, params);
  }

  return {
    navigateTo,
    getParams,
    getSearch,
    navigateBack,
    clearAllStack,
    navigateBackTo,
    redirectTo,
    getCurrent,
    getAllStack,
    reload,
    onChange: maxRouterHistory.onRouteChangeEmitter.event,
    maxRouterHistory,
  };
}
